import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk } from 'ff/debrief/store';
import { findCurrentSegment, TIME_INTERVAL } from 'ff/debrief/utils';
import { tracklogApi } from 'ff/debrief/features/TracklogApi';
import * as AppStore from 'ff/debrief/features/AppSlice';

type Timer = ReturnType<typeof setInterval>;

export interface ChronoState {
    currentOffset: number;
    currentPoint: number[];
    tickerSpeed: number;
    ticker: Timer;
}

const initialChronoState: ChronoState = {
    currentOffset: 0,
    currentPoint: null,
    ticker: null,
    tickerSpeed: 1,
};

export const chronoSlice = createSlice({
    name: 'ChronoSlice',
    initialState: initialChronoState,
    reducers: {
        setTicker: (state, action: PayloadAction<Timer>) => {
            state.ticker = action.payload;
        },
        setTickerSpeed: (state, action: PayloadAction<number>) => {
            state.tickerSpeed = action.payload;
        },
        pauseTicker: state => {
            clearInterval(state.ticker);
            state.ticker = null;
        },
        skip: (state, action: PayloadAction<number>) => {
            state.currentOffset += action.payload;
        },
        setOffset: (state, action: PayloadAction<number>) => {
            state.currentOffset = action.payload;
        },
        reset: state => {
            clearInterval(state.ticker);
            state.currentOffset = 0;
            state.ticker = null;
            state.currentPoint = null;
        },
    },
});

export const actions = chronoSlice.actions;

export function playTicker(currentTracklogUUID: string): AppThunk {
    return (dispatch, getState) => {
        const isTicking = getState().chronoSlice.ticker;
        const tickRate = TIME_INTERVAL / getState().chronoSlice.tickerSpeed;
        if (!isTicking) {
            const intervalId = setInterval(() => {
                dispatch(actions.skip(1));
                const cachedDebriefQuery =
                    tracklogApi.endpoints.getDebriefByTrackUUID.select(
                        currentTracklogUUID
                    )(getState());
                const currSeg = findCurrentSegment(
                    cachedDebriefQuery.data?.debriefSegments,
                    getState().chronoSlice.currentOffset
                );
                dispatch(AppStore.actions.setSelectedSegment(currSeg));
            }, tickRate);
            dispatch(actions.setTicker(intervalId));
        }
    };
}

export function setSpeed(speed: number): AppThunk {
    return (dispatch, getState) => {
        dispatch(actions.setTickerSpeed(speed));

        const isTicking = getState().chronoSlice.ticker;
        const tickRate = TIME_INTERVAL / speed;

        if (isTicking) {
            clearInterval(isTicking);
            const intervalId = setInterval(() => {
                dispatch(actions.skip(1));
            }, tickRate);
            dispatch(actions.setTicker(intervalId));
        }
    };
}
